import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import { LogoBlock } from "../components/LogoBlock/LogoBlock";
import { reportThemes as themes } from "../themes";
import { BackgroundBlocks } from "../components/BackgroundBlocks/BackgroundBlocks";
import { FooterBlock } from "../components/FooterBlock/FooterBlock";
import { Body } from "../components/Body/Body";
import {
  BodyHeader,
  BodyHeaderItem,
} from "../components/BodyHeader/BodyHeader";
import { Table } from "../components/Table/Table";
import { TextArea } from "../components/TextArea/TextArea";
import generalStyle from "../styles";
import { decimalPlaces } from "../../../../helpers/mealsHelper";
import { calcStudyDrugPercentage } from "./utils";
import { CheckBoxContainer } from "../components/CheckBoxContainer/CheckBoxContainer";

const headersTable1 = [
  {
    value: "Date Range Average",
    width: "50%",
    textAlign: "left",
  },
  {
    value: "Complete",
    width: "25%",
    textAlign: "right",
  },
  {
    value: "Sick Day",
    width: "25%",
    textAlign: "right",
  },
];

const headersTable2 = [
  {
    value: "Date/time",
    width: "25%",
    textAlign: "left",
  },
  {
    value: "Complete",
    width: "15%",
    textAlign: "right",
  },
  {
    value: "Sick day",
    width: "15%",
    textAlign: "right",
  },
  {
    value: "Study Drug (% DCI)",
    width: "25%",
    textAlign: "right",
  },
  {
    value: "Study Drug (ml)",
    width: "20%",
    textAlign: "right",
  },
];

const reportStyle = StyleSheet.create({
  note: {
    ...generalStyle.bold,
    fontSize: 8,
    marginTop: 7,
  },
  text: {
    fontSize: 10,
  },
  defaultMargin: {
    marginTop: 10,
  },
  signature: {
    marginLeft: 2,
  },
  signtureTitle: {
    marginLeft: 5,
  },
  weekSentece: {
    marginLeft: 4,
  },
});

const ToleratedDoseStudyDrugReport = (data, { theme = "rarify" }) => {
  const {
    participantID,
    generatedDate,
    appStudy,
    dateRange,
    totalPerDay,
    totalsData,
  } = data;
  const themeData = themes[theme];

  return (
    <Document data-testid="tolerated-dose-study-drug-report">
      <Page size="A4" style={generalStyle.page}>
        <View fixed>
          <BackgroundBlocks {...{ themeData }} />
          <LogoBlock {...{ themeData }} />
        </View>
        <Body
          {...{ themeData }}
          title="Verification of Tolerated Dose of Study Drug"
        >
          <BodyHeader {...{ themeData }}>
            <BodyHeaderItem
              title="Protocol Number:"
              subTitle={appStudy.toUpperCase()}
            />
            <BodyHeaderItem title="Participant ID:" subTitle={participantID} />
          </BodyHeader>
          <Table headers={headersTable1} {...{ themeData }}>
            <View>
              <Text
                style={{
                  ...generalStyle.semiBold,
                  ...reportStyle.text,
                }}
              >
                {dateRange}
              </Text>
              <Text
                style={{
                  ...reportStyle.text,
                  ...generalStyle.semiBold,
                }}
              >
                {totalsData.completedDays} day(s)
              </Text>
              <Text
                style={{
                  ...reportStyle.text,
                  ...generalStyle.semiBold,
                }}
              >
                {totalsData.sickDays} day(s)
              </Text>
            </View>
          </Table>
          <Table headers={headersTable2} {...{ themeData }}>
            {totalPerDay.map((row, index) => (
              <View key={index}>
                <Text
                  style={{
                    ...generalStyle.bold,
                    ...reportStyle.text,
                  }}
                >
                  {row.date}
                </Text>
                <Text
                  style={{
                    ...reportStyle.text,
                    ...generalStyle.semiBold,
                  }}
                >
                  {row.completed}
                </Text>
                <Text
                  style={{
                    ...reportStyle.text,
                  }}
                >
                  {row.sickDay}
                </Text>
                <Text
                  style={{
                    ...reportStyle.text,
                  }}
                >
                  {Number(
                    calcStudyDrugPercentage(row.studyDrug, row.totalCalories)
                  ).toFixed(decimalPlaces)}
                </Text>
                <Text
                  style={{
                    ...reportStyle.text,
                  }}
                >
                  {Number(row.studyDrugQty).toFixed(decimalPlaces)}
                </Text>
              </View>
            ))}
          </Table>
          <View style={generalStyle.marginTopAuto}>
            <CheckBoxContainer
              optionsLeft={[""]}
              text="The tolerated dose of study drug for this subject has been
                determined."
            />
            <Text
              style={{
                ...reportStyle.text,
                ...reportStyle.defaultMargin,
              }}
            >
              The dose of study drug is _______________ ml/day which is
              _______________ % of DCI.
            </Text>
            <View style={{ ...reportStyle.defaultMargin }} />
            <TextArea text="Comments by Principal Investigator:" />
            <View
              style={{
                ...generalStyle.inLineBlocks,
                ...reportStyle.defaultMargin,
              }}
            >
              <View
                style={{
                  ...generalStyle.inLineBlocks,
                }}
              >
                <Text style={{ ...reportStyle.text, ...generalStyle.semiBold }}>
                  Date of Verification by Principal Investigator:
                </Text>
                <Text
                  style={{
                    ...reportStyle.text,
                    ...reportStyle.signature,
                  }}
                >
                  __ __ / __ __ __ / __ __ __ __
                </Text>
              </View>
            </View>
            <View
              style={{
                ...generalStyle.inLineBlocks,
                ...reportStyle.defaultMargin,
              }}
            >
              <Text
                style={{
                  ...reportStyle.text,
                  ...generalStyle.semiBold,
                }}
              >
                PI Name:
              </Text>
              <Text style={reportStyle.text}>
                _________________________________________
              </Text>
              <Text
                style={{
                  ...reportStyle.text,
                  ...generalStyle.semiBold,
                  ...reportStyle.signature,
                }}
              >
                PI Signature:
              </Text>
              <Text style={{ ...reportStyle.text, ...reportStyle.signature }}>
                _________________________________________
              </Text>
            </View>
          </View>
        </Body>
        <FooterBlock fixed {...{ themeData }} {...{ generatedDate }} />
      </Page>
    </Document>
  );
};

export default ToleratedDoseStudyDrugReport;
